import React from 'react';
import { QuoteData } from '../../types';
import { formatCurrency, formatPercent, calculateLTV } from '../../lib/calculations';
import { motion } from 'framer-motion';
interface EquityBreakdownProps {
  data: QuoteData;
}
export function EquityBreakdown({
  data
}: EquityBreakdownProps) {
  const ltv = calculateLTV(data.proposedLoanAmount, data.propertyValue);
  const equityKept = data.propertyValue - data.proposedLoanAmount;
  // Calculate percentages for the bar
  const newLoanPct = data.currentBalance / data.propertyValue * 100;
  const cashOutPct = data.cashToBorrower / data.propertyValue * 100;
  const equityPct = equityKept / data.propertyValue * 100;
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    delay: 0.6,
    duration: 0.8,
    ease: 'easeOut'
  }} className="bg-charcoal-800 rounded-2xl border border-white/10 p-6 md:p-8" data-id="element-13">
      <div className="text-[10px] font-medium tracking-[0.2em] text-gold mb-6 flex items-center gap-3" data-id="element-14">
        <span className="text-gold/50" data-id="element-15">—</span> WHERE THE CASH COMES FROM
      </div>

      <p className="text-gray-400 mb-8 max-w-3xl font-light text-sm leading-relaxed" data-id="element-16">
        Your home value has appreciated. You're tapping a portion of that
        equity, not all of it, and your new loan stays well within lending
        guidelines.
      </p>

      <div className="mb-3 flex justify-between items-end" data-id="element-17">
        <span className="text-xs text-gray-500 font-light uppercase tracking-widest" data-id="element-18">
          Home value
        </span>
        <span className="text-white" data-id="element-19">{formatCurrency(data.propertyValue)}</span>
      </div>

      {/* Stacked Bar */}
      <div className="h-4 w-full flex rounded-sm overflow-hidden mb-8 gap-[1px] bg-charcoal-900" data-id="element-20">
        <div style={{
        width: `${newLoanPct}%`
      }} className="bg-slate-600 h-full transition-all duration-1000" data-id="element-21"></div>
        <div style={{
        width: `${cashOutPct}%`
      }} className="bg-gold h-full transition-all duration-1000" data-id="element-22"></div>
        <div style={{
        width: `${equityPct}%`
      }} className="bg-charcoal-700 h-full transition-all duration-1000" data-id="element-23"></div>
      </div>

      {/* Legend */}
      <div className="grid grid-cols-3 gap-4 mb-8" data-id="element-24">
        <div data-id="element-25">
          <div className="flex items-center gap-2 mb-2" data-id="element-26">
            <div className="w-2 h-2 bg-slate-600 rounded-sm" data-id="element-27"></div>
            <span className="text-[10px] text-gray-500 uppercase tracking-[0.15em]" data-id="element-28">
              New Loan
            </span>
          </div>
          <div className="text-sm text-white" data-id="element-29">
            {formatCurrency(data.proposedLoanAmount)}
          </div>
        </div>
        <div data-id="element-30">
          <div className="flex items-center gap-2 mb-2" data-id="element-31">
            <div className="w-2 h-2 bg-gold rounded-sm" data-id="element-32"></div>
            <span className="text-[10px] text-gray-500 uppercase tracking-[0.15em]" data-id="element-33">
              Cash Out
            </span>
          </div>
          <div className="text-sm text-gold" data-id="element-34">
            {formatCurrency(data.cashToBorrower)}
          </div>
        </div>
        <div data-id="element-35">
          <div className="flex items-center gap-2 mb-2" data-id="element-36">
            <div className="w-2 h-2 bg-charcoal-700 rounded-sm" data-id="element-37"></div>
            <span className="text-[10px] text-gray-500 uppercase tracking-[0.15em]" data-id="element-38">
              Equity Kept
            </span>
          </div>
          <div className="text-sm text-white" data-id="element-39">{formatCurrency(equityKept)}</div>
        </div>
      </div>

      <div className="pt-6 border-t border-white/10 flex justify-between items-center text-sm" data-id="element-40">
        <span className="text-gray-500 font-light" data-id="element-41">New loan-to-value</span>
        <span className="text-white" data-id="element-42">{formatPercent(ltv)}</span>
      </div>
    </motion.div>;
}